import { useContext, createContext, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MoreVertical,
  ChevronLast,
  ChevronFirst,
  Home,
  LogOut,
  Settings,
  Bell,
  Globe,
  Shield,
  Database,
  HelpCircle,
  Sun,
  Moon,
  Eye,
  Briefcase,
  Crown,
  Newspaper
} from "lucide-react";
import PeopleIcon from "./peopleIcon";
import Add from "./Add";
import Communities from "./Communites";
import ChartsIcon from "./ChartsIcon";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { logoutUser as logoutThunk } from "../../redux/authentication/utils/core/authslice";
import { NavMenuItem, LogoutButton } from "../profileDropDown/dropDown";
import { useTheme } from "../../theme/ThemeContext";
import UserAvatar from "../UserAvatar/UserAvatar";

/* ✅ CONTEXT */
const SidebarContext = createContext({ expanded: true });

/* ✅ PERSISTENT SIDEBAR STATE */
const usePersistentSidebarState = () => {
  const [expanded, setExpanded] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("sidebar-expanded");
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("sidebar-expanded", JSON.stringify(expanded));
    }
  }, [expanded]);

  return [expanded, setExpanded];
};

export default function Sidebar({ children }) {
  const { t } = useTranslation();
  const { isDark, toggleTheme } = useTheme();
  const [expanded, setExpanded] = usePersistentSidebarState();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.profile);
  const featureFlags = useSelector((state) => state.featureFlags.flags);
  const location = useLocation();
  const activePage = location.pathname;

  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await dispatch(logoutThunk()).unwrap();
      navigate("/login", { replace: true });
    } catch (err) {
      navigate("/login", { replace: true });
    }
  };

  return (
    <aside
      style={{ position: "sticky", top: "64px", height: "calc(100vh - 64px)" }}
      className="h-screen z-[10000]"
    >
      <nav aria-label="Main navigation" className="h-full flex flex-col bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700 shadow-sm">

        {/* TOGGLE BUTTON */}
        <div className="p-4 pb-2 flex justify-between items-center">
          <button
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
            className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
          >
            {expanded ? (
              <ChevronFirst className="text-gray-600 dark:text-gray-300" />
            ) : (
              <ChevronLast className="text-gray-600 dark:text-gray-300" />
            )}
          </button>
          {expanded && (
            <button
              onClick={toggleTheme}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
            >
              {isDark ? (
                <Sun className="w-[18px] h-[18px] text-amber-400" />
              ) : (
                <Moon className="w-[18px] h-[18px] text-indigo-600" />
              )}
            </button>
          )}
        </div>

        <SidebarContext.Provider value={{ expanded }}>
          <ul className="flex-1 px-3">

            <SidebarItem to="/dashboard" icon={<Home />} text={t('sidebar.dashboard')} active={activePage === "/dashboard"} />

            {featureFlags.posts && (
              <SidebarItem to="/stockers" icon={<PeopleIcon />} text={t('sidebar.posts')} active={activePage === "/stockers"} />
            )}

            <SidebarItem to="/groupies" icon={<Communities />} text={t('sidebar.groups')} active={activePage === "/groupies"} />

            {featureFlags.posts && (
              <SidebarItem to="/post-create" icon={<Add />} text={t('sidebar.post')} active={activePage === "/post-create"} />
            )}

            <SidebarItem to="/stocks-overview" icon={<ChartsIcon />} text={t('sidebar.stocks')} active={activePage === "/stocks-overview"} />

            <SidebarItem to="/watchlist" icon={<Eye />} text={t('sidebar.watchlist')} active={activePage === "/watchlist"} />

            <SidebarItem to="/portfolio" icon={<Briefcase />} text={t('sidebar.portfolio')} active={activePage === "/portfolio"} />

            <SidebarItem to="/leaderboard" icon={<Crown />} text={t('sidebar.leaderboard')} active={activePage === "/leaderboard"} />

            <SidebarItem to="/news" icon={<Newspaper />} text={t('sidebar.news')} active={activePage === "/news"} />

            <SidebarItem to="/notifications" icon={<Bell />} text={t('sidebar.notifications')} active={activePage === "/notifications"} />

            <SidebarItem to="/profile" icon={<Settings />} text={t('sidebar.profile')} active={activePage === "/profile"} />

            <SidebarItem onClick={handleLogout} icon={<LogOut />} text={t('sidebar.logout')} />

            {children}
          </ul>
        </SidebarContext.Provider>

        {/* USER FOOTER */}
        <div className="relative border-t border-gray-200 dark:border-gray-700 flex p-3" ref={accountMenuRef}>
          <Link to="/profile" className="relative flex-shrink-0">
            <UserAvatar userId={user?.id} username={user?.username} className="w-10 h-10 shadow-inner" textClass="text-sm" />
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
          </Link>

          <div
            className={`flex justify-between items-center overflow-hidden transition-all ${
              expanded ? "w-52 ml-3" : "w-0"
            }`}
          >
            <div className="leading-tight">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate max-w-[170px]">
                {user?.username || "Loading..."}
              </h4>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[170px] block">
                {user?.email}
              </span>
            </div>
            <button
              onClick={() => setShowAccountMenu((open) => !open)}
              aria-label="Account menu"
              aria-expanded={showAccountMenu}
              aria-haspopup="true"
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 flex-shrink-0"
            >
              <MoreVertical size={18} className="text-indigo-600 dark:text-indigo-400" />
            </button>
          </div>

          {/* Account menu -- opens upward since this sits at the bottom of the viewport */}
          {showAccountMenu && (
            <div
              role="menu"
              aria-label="Account menu"
              className="absolute bottom-full right-3 mb-2 w-[min(16rem,calc(100vw-2rem))] bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-800 rounded-lg shadow-xl py-2 z-50"
            >
              <NavMenuItem
                icon={<Globe className="w-5 h-5" />}
                text={t('sidebar.language', 'Language')}
                to="/settings/language"
                onClick={() => setShowAccountMenu(false)}
              />
              {user?.role === 'admin' && (
                <NavMenuItem
                  icon={<Shield className="w-5 h-5" />}
                  text={t('sidebar.adminDashboard', 'Admin Dashboard')}
                  to="/admin"
                  onClick={() => setShowAccountMenu(false)}
                />
              )}
              <NavMenuItem
                icon={<Database className="w-5 h-5" />}
                text={t('sidebar.yourData', 'Your data')}
                to="/settings/your-data"
                onClick={() => setShowAccountMenu(false)}
              />
              <NavMenuItem
                icon={<Settings className="w-5 h-5" />}
                text={t('sidebar.allSettings', 'All settings')}
                to="/settings"
                onClick={() => setShowAccountMenu(false)}
              />
              <div className="border-t border-gray-800 mt-2 pt-2">
                <NavMenuItem
                  icon={<HelpCircle className="w-5 h-5" />}
                  text={t('sidebar.helpCenter', 'Help center')}
                  to="/settings/help-center"
                  onClick={() => setShowAccountMenu(false)}
                />
                <LogoutButton onClick={() => setShowAccountMenu(false)} />
              </div>
            </div>
          )}
        </div>

      </nav>
    </aside>
  );
}

/* ✅ SIDEBAR ITEM */
export function SidebarItem({ icon, text, active, alert, to, onClick }) {
  const { expanded } = useContext(SidebarContext);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const itemRef = useRef(null);

  useEffect(() => {
    if (showTooltip && itemRef.current && !expanded) {
      const rect = itemRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: rect.right + 8,
        y: rect.top + rect.height / 2
      });
    }
  }, [showTooltip, expanded]);

  const tooltipElement = !expanded && showTooltip && (
    <div
      className="fixed bg-indigo-800 text-indigo-100 px-2 py-1 rounded-md text-sm pointer-events-none transform -translate-y-1/2 shadow-lg border border-indigo-700"
      style={{ left: tooltipPosition.x, top: tooltipPosition.y, zIndex: 50000 }}
    >
      {text}
    </div>
  );

  const content = (
    <>
      <div className={active ? "text-indigo-700 dark:text-indigo-200" : "text-indigo-500 dark:text-indigo-400"}>
        {icon}
      </div>
      <span className={`overflow-hidden transition-all ${expanded ? "w-52 ml-3" : "w-0"}`}>
        {text}
      </span>
      {alert && (
        <div className={`absolute right-2 w-2 h-2 rounded bg-indigo-400 ${expanded ? "" : "top-2"}`} />
      )}
    </>
  );

  // The interactive element (Link or button) must be inside the <li>, not
  // wrapping it -- a <ul> that has an <a>/<div> as its own direct child,
  // with <li> nested *inside* that, is invalid list semantics (axe's
  // list/listitem rules; found by the pass-28 accessibility sweep). Every
  // call site used to wrap <SidebarItem> in <Link>/<div onClick> instead of
  // passing `to`/`onClick` in as props -- fixed here, not just cosmetically.
  const itemClassName = `
    relative flex items-center w-full py-2 px-3 font-medium rounded-md cursor-pointer transition-colors text-left
    ${active
      ? "bg-indigo-100 text-indigo-900 dark:bg-indigo-900 dark:text-indigo-100"
      : "text-gray-600 hover:bg-indigo-50 dark:text-gray-300 dark:hover:bg-indigo-900/40"}
  `;

  return (
    <>
      <li
        ref={itemRef}
        onMouseEnter={() => !expanded && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="relative my-1"
      >
        {to ? (
          <Link to={to} className={itemClassName}>{content}</Link>
        ) : (
          <button type="button" onClick={onClick} className={itemClassName}>{content}</button>
        )}
      </li>
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  );
}

export { SidebarContext };